/**
 * Google Analytics 4 (gtag.js) — page-view reporting for the SPA.
 *
 * The gtag.js tag itself is a static `<head>` install (scripts/gaHead.ts,
 * stamped into every route by scripts/prerender.ts and by vite.config.ts's dev
 * plugin). It loads only on the production hostname (see `isProductionHost`
 * below and `scripts/gaHead.ts`), ahead of any consent answer — the *script
 * load* is not gated, the same pattern the AdSense base script uses.
 *
 * **Corrected 2026-09-18** — an earlier version of this comment (and of
 * `scripts/consentModeHead.ts` and `/privacy`) said there was no consent or
 * Do-Not-Track gate on Google Analytics anywhere in this codebase. That is no
 * longer true: `scripts/consentModeHead.ts` now starts `analytics_storage`
 * `denied` by default, and `src/lib/ads.ts`'s `pushConsentModeUpdate` grants
 * or denies it together with the ad signals, from the same `ConsentBanner`
 * answer (or a Do-Not-Track/GPC refusal). Until granted, gtag.js does not set
 * or read an analytics cookie — Google instead falls back to cookieless,
 * modelled pings, which is what this file's calls below still trigger
 * regardless of consent state; the storage restriction happens inside
 * Google's own tag, not in this file. See the Analytics section of
 * `/privacy` for what that means for a visitor.
 *
 * The head tag sets `send_page_view: false`, so GA never reports a page on its
 * own. This file's only job is telling it to, once per route change — a plain
 * `<head>` tag would otherwise miss every soft navigation in the SPA, since
 * gtag.js only knows about the page it was loaded on.
 */

import { SITE } from '@/config/site'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

/**
 * The one real production hostname, derived from `SITE.origin` rather than
 * hand-maintained separately — this and `scripts/gaHead.ts`'s copy of the same
 * derivation can never disagree, because both trace back to the single
 * `SITE.origin` value (see that file's own comment on why config drift like
 * this matters).
 *
 * `www` is deliberately excluded: production 301-redirects `www` to the apex
 * at the host level (`public/.htaccess`) before any HTML is ever returned, so
 * no browser can execute this script on `www` in real traffic. Adding it here
 * would widen the allowlist for a request path that cannot occur.
 */
const PRODUCTION_HOST = new URL(SITE.origin).hostname

/**
 * True only for the exact production hostname — an explicit allowlist rather
 * than a "not localhost" denylist, so every current and future non-production
 * host (localhost, 127.0.0.1, any `.local` machine name, Netlify/Cloudflare
 * preview aliases, a CI runner's hostname, a decommissioned deployment domain)
 * is excluded by construction instead of needing to be named individually.
 * Exact string equality (not `.endsWith`) also blocks a host like
 * `atsresumekit.com.evil.example` from matching by suffix.
 */
export const isProductionHost = (hostname: string | undefined | null): boolean =>
  typeof hostname === 'string' && hostname.trim().toLowerCase().replace(/\.$/, '') === PRODUCTION_HOST

/**
 * Configured *and* running on production. Every exported function in this
 * file funnels through this one check, so a local `vite dev`/`vite preview`
 * run, a Playwright/CI run, or a preview deployment can never report a real
 * event against the live GA4 property — even though they all serve the exact
 * same prerendered HTML/JS as production.
 */
const gaConfigured = (): boolean =>
  SITE.gaMeasurementId.trim() !== '' &&
  typeof window !== 'undefined' &&
  isProductionHost(window.location.hostname)

/**
 * Reports one page view. Route-aware, so a soft navigation is recorded the same
 * way a hard load is.
 *
 * `window.gtag` is defined synchronously by the inline half of the `<head>`
 * install (before the external gtag.js has necessarily finished loading — that
 * script is `async`), so it is already present by the time React mounts and
 * this runs, on production. The `!window.gtag` check is defence in depth: the
 * real gate is `gaConfigured()` above, which already covers a missing
 * measurement id and a non-production hostname (where the head tag skips its
 * own `gtag` setup entirely — see `scripts/gaHead.ts`).
 */
export const sendPageView = (path: string, title?: string): void => {
  if (!gaConfigured() || typeof window === 'undefined' || !window.gtag) return
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    ...(title ? { page_title: title } : {}),
  })
}

/** Names of the product events `sendEvent` reports. */
export type GaEvent =
  | 'builder_started'
  | 'resume_uploaded'
  | 'ats_score_shown'
  | 'resume_exported'
  | 'contact_form_submitted'

/**
 * Reports one product event. Same guard as `sendPageView` — a no-op with no
 * measurement id configured, or before gtag.js has attached.
 */
export const sendEvent = (name: GaEvent): void => {
  if (!gaConfigured() || typeof window === 'undefined' || !window.gtag) return
  window.gtag('event', name)
}

/**
 * The transport for src/lib/keyEvents.ts, which owns the event names, the
 * parameter types and the consent check. Use `sendKeyEvent` there, not this.
 */
export const sendGaEventWithParams = (name: string, params: Record<string, string>): void => {
  if (!gaConfigured() || typeof window === 'undefined' || !window.gtag) return
  window.gtag('event', name, params)
}
